import { z } from 'zod';

import { zodTemporalDuration, zodTemporalPlainTime } from '@willow/types-iso';

import { Expression } from '../../Expressions/ExpressionEvaluator';

/**
 * Retry
 */
export const RetryNever = z.object({
  type: z.literal('retryNever'),
});

export type RetryNever = z.infer<typeof RetryNever>;

export const RetryAfterDuration = z.object({
  type: z.literal('retryAfterDuration'),
  times: z.number().optional(),
  duration: zodTemporalDuration.meta({
    description: 'The duration after which the step will retry upon failure in ISO 8601 Duration format.',
    example: 'P1D',
    type: 'string',
  }),
  timeOfDay: zodTemporalPlainTime.optional().meta({
    description: 'The time of day at which the step will retry upon failure in ISO 8601 Time format.',
    example: '14:30:00',
    type: 'string',
  }),
});

export type RetryAfterDuration = z.infer<typeof RetryAfterDuration>;

export const RetryAfterDurationExpression = z.object({
  type: z.literal('retryAfterDurationExpression'),
  times: z.number().optional(),
  expression: Expression('duration').meta({
    description: 'An expression that evaluates to a ISO 8601 Duration after which the step will retry upon failure.',
    example: 'if(loanHasRepaymentPlan(), P1D, P2D)',
  }),
  timeOfDay: zodTemporalPlainTime.optional().meta({
    description: 'The time of day at which the step will retry upon failure in ISO 8601 Time format.',
    example: '14:30:00',
    type: 'string',
  }),
});

export const RetryOnDateExpression = z.object({
  type: z.literal('retryOnDateExpression'),
  times: z.number().optional(),
  expression: Expression('zonedDateTime').meta({
    description: 'An expression that evaluates to a ZonedDateTime after which the step will retry upon failure.',
    example: 'zonedDateTime(plainDateFromForm(someFormDefinitionId, "$.pathToDate"))',
  }),
});
export type RetryOnDateExpression = z.infer<typeof RetryOnDateExpression>;

export const RetryDefinition = z.discriminatedUnion('type', [
  RetryNever,
  RetryAfterDuration,
  RetryAfterDurationExpression,
  RetryOnDateExpression,
]);
export type RetryDefinition = z.infer<typeof RetryDefinition>;
